import { cn } from '../../lib/cn.js'
import { Text } from '../ui/Typography.js'

/**
 * A section heading: a small word between two fading hairlines.
 *
 * `.fawn-ruled` in the kit, and the thing the prototype's class record uses to
 * separate "Who was there" from "What was asked":
 *
 * ```css
 * .fawn-ruled { display:flex; align-items:center; gap: var(--fawn-space-5); }
 * .fawn-ruled::before, ::after { content:""; flex:1; height:1px; }
 * .fawn-ruled::before { background: linear-gradient(90deg, transparent, var(--fawn-hairline)); }
 * ```
 *
 * Every section on every record was a `body-strong` heading sitting on the
 * left with nothing marking where the last one ended — which is legible and
 * says nothing about structure. This says "a new part starts here" without
 * drawing a box round it.
 *
 * The rules fade rather than running edge to edge on purpose: a full-width
 * hairline reads as a divider between two equal things, and these are a
 * heading over the thing beneath it.
 */
export function RuledHeading({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex items-center gap-5', className)}>
      <span
        aria-hidden
        className="from-hairline h-px flex-1 bg-gradient-to-l from-0% to-transparent"
      />
      <Text as="h2" variant="eyebrow" className="text-ink-muted shrink-0">
        {children}
      </Text>
      <span
        aria-hidden
        className="from-hairline h-px flex-1 bg-gradient-to-r from-0% to-transparent"
      />
    </div>
  )
}
